<template>
  <div class="preview-block">
    <div class="preview-header">
      <h3>Preview: {{ fileName }}</h3>
      <button
        class="btn-3"
        title="Close Preview"
        aria-label="close"
        @click="$emit('closePreview')"
        data-testing="close-right-sidebar"
      >
        <SVGIcon :path="mdiClose" type="is-white" size="24" />
      </button>
    </div>
    <div class="preview-container">
      <img
        v-if="checkIfExternalContentIsImage()"
        class="content"
        :src="getContentLink()"
        alt="image"
      />
      <iframe
        v-else-if="getContentLink()"
        class="content"
        :src="getContentLink()"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { mdiClose } from "@mdi/js";
import { computed } from "vue";

import { useUIContentStore } from "@/stores/ui-content.store";

import { getFileNameFromURL, isSafeUrl } from "@/utils/file-helpers";

const uiContentStore = useUIContentStore();

const getContentLink = (): string => {
  const link = getEmbeddableLink();
  return isSafeUrl(link) ? link : "";
};
const getEmbeddableLink = (): string => {
  if (uiContentStore.fileLocationForPreview) {
    if (uiContentStore.fileLocationForPreview.includes("youtu")) {
      const linkWithoutPostfix =
        uiContentStore.fileLocationForPreview.split("&")[0];
      const prefixesToSearch = [
        "watch?v=",
        "v/",
        "watch?vi=",
        "vi/",
        "e/",
        "?v="
      ];
      const searchValue = prefixesToSearch.find((element: string) => {
        return linkWithoutPostfix.includes(element);
      });
      if (linkWithoutPostfix.includes("youtube")) {
        return searchValue
          ? linkWithoutPostfix.replace(searchValue, "embed/")
          : "";
      }
      if (linkWithoutPostfix.includes("youtu.be")) {
        return (
          "https://www.youtube.com/embed/" +
          linkWithoutPostfix.split("https://youtu.be")[1]
        );
      }
    }
    if (uiContentStore.fileLocationForPreview.includes("onedrive")) {
      return uiContentStore.fileLocationForPreview.replaceAll("&amp;", "&");
    }
    return uiContentStore.fileLocationForPreview;
  }
  return "";
};
const checkIfExternalContentIsImage = (): boolean => {
  const contentLink = getContentLink();
  return !!contentLink && contentLink.match(/\.(jpeg|jpg|gif|png)$/) !== null;
};

const fileName = computed<string>(() => {
  if (!uiContentStore.fileLocationForPreview) {
    return "";
  }
  if (uiContentStore.fileLocationForPreview.includes("youtu")) {
    const videoId = getContentLink().split("embed/")[1];
    return videoId ? `YouTube video: ${videoId}` : "YouTube video";
  }
  return getFileNameFromURL(uiContentStore.fileLocationForPreview);
});
</script>

<style lang="scss" scoped>
.preview-block {
  width: 50%;
  height: 100%;
  position: absolute;
  right: $right-sidebar-width-sm;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-top-left-radius: $border-radius;
  border-bottom-left-radius: $border-radius;
  transition: width 0.3s ease;
  @media (min-width: $breakpoint-md) {
    right: $right-sidebar-width-md;
  }
}
.preview-header {
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 0;
  padding: 0 2rem;
  height: 3rem;
  background-color: $primary-color;
  border-top-left-radius: $border-radius;
  button {
    color: white;
  }
}
.preview-container {
  overflow: hidden;
  flex-shrink: 1;
  flex-grow: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.content {
  display: block;
  max-width: 100%;
}
iframe.content {
  width: 100%;
  height: 100%;
}
</style>
